import { config } from "@/config/environment";
import { LogLevel, type LogContext } from "@/types/core";

class AuthLogger {
  private isDevelopment = import.meta.env.DEV;
  private isDebugEnabled = config.debug.enabled;

  private shouldLog(level: LogLevel): boolean {
    if (level === LogLevel.ERROR) return true; // Always log errors
    if (level === LogLevel.WARN)
      return this.isDevelopment || this.isDebugEnabled;
    if (level === LogLevel.INFO)
      return this.isDevelopment || this.isDebugEnabled;
    if (level === LogLevel.DEBUG)
      return this.isDevelopment && this.isDebugEnabled;
    return false;
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): string {
    const timestamp = new Date().toISOString();
    const prefix = `[AUTH:${level.toUpperCase()}]`;
    const contextStr = context ? ` ${JSON.stringify(context)}` : "";
    return `${timestamp} ${prefix} ${message}${contextStr}`;
  }

  private logToConsole(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): void {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(level, message, context);

    switch (level) {
      case LogLevel.ERROR:
        console.error(formattedMessage);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage);
        break;
      case LogLevel.DEBUG:
        console.debug(formattedMessage);
        break;
    }
  }

  // Public logging methods
  error(message: string, context?: LogContext): void {
    this.logToConsole(LogLevel.ERROR, message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.logToConsole(LogLevel.WARN, message, context);
  }

  info(message: string, context?: LogContext): void {
    this.logToConsole(LogLevel.INFO, message, context);
  }

  debug(message: string, context?: LogContext): void {
    this.logToConsole(LogLevel.DEBUG, message, context);
  }

  // Specialized logging methods for common auth scenarios
  msalInit(message: string, context?: LogContext): void {
    this.debug(`MSAL Init: ${message}`, { ...context, component: "MSAL" });
  }

  sessionRestore(message: string, context?: LogContext): void {
    this.info(`Session Restore: ${message}`, {
      ...context,
      component: "Session"
    });
  }

  tokenValidation(message: string, context?: LogContext): void {
    this.debug(`Token Validation: ${message}`, {
      ...context,
      component: "Token"
    });
  }

  roleExtraction(message: string, context?: LogContext): void {
    this.debug(`Role Extraction: ${message}`, {
      ...context,
      component: "Roles"
    });
  }

  redirect(message: string, context?: LogContext): void {
    this.info(`Redirect: ${message}`, { ...context, component: "Redirect" });
  }

  cache(message: string, context?: LogContext): void {
    this.debug(`Cache: ${message}`, { ...context, component: "Cache" });
  }

  // Error logging with enhanced context
  authError(
    error: unknown,
    context: string,
    additionalContext?: LogContext
  ): void {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorContext = {
      ...additionalContext,
      component: context,
      errorType: error instanceof Error ? error.constructor.name : typeof error,
      stack: error instanceof Error ? error.stack : undefined
    };

    this.error(
      `Authentication error in ${context}: ${errorMessage}`,
      errorContext
    );
  }
}

// Export singleton instance
export const authLogger = new AuthLogger();

// Export the class for testing
export { AuthLogger };
